import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { createContext, FC, useRef } from "react";
import ConditionalRender from "../ConditionalRender";
import Breadcrumb from "./Components/breadcrumb";
import ParentAssets from "./Components/ParentAssets";
import PlantSelector from "./Components/PlantSelector";
import { PrimaryIcons } from "./Components/PrimaryIcons";
import SecondaryMenu from "./Components/SecondaryMenu";
import {
  SecondaryHeaderContextType,
  SecondaryHeaderProps,
} from "./interfaces";
import DateRangeSelector from "./Components/DateRangeSelector";
import Chips from "./Components/Chips";
import { isUndefined } from "lodash";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const SecondaryHeaderContext = createContext<SecondaryHeaderContextType>(
  {} as SecondaryHeaderContextType
);

const SecondaryHeader: FC<SecondaryHeaderProps> = ({
  title,
  render = true,
  backButtonConfig,
  plantSelectorConfig,
  primaryIcons,
  secondaryMenu,
  parentAssetsConfig,
  dateRangeConfig,
  breadcrumbItems = [],
  chips = [],
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const from = location.state?.from as string | undefined;
  const showBackButton = from?.startsWith("/dashboard/") ?? false;

  const childrenContainerRef = useRef<HTMLDivElement>(null);
  const shouldRenderSecondaryMenu =
    (secondaryMenu?.length &&
      !secondaryMenu.every(({ render }) => !isUndefined(render) && !render)) ||
    parentAssetsConfig?.assets?.length;

  const scrollToTop = () => {
    childrenContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SecondaryHeaderContext.Provider
      value={{ childrenContainerRef, scrollToTop }}
    >
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        position='relative'
        id="secondary-header"
      >
        {render ? (
          <>
            <Grid container p={1} alignItems="center" sx={{ height: 64 }}>
              {Boolean(backButtonConfig) || showBackButton && (
                <Grid item mr={1}>
                  <IconButton {...backButtonConfig?.iconButtonProps} onClick={() => {
                    if (showBackButton) {
                      navigate(from!);
                    }
                  }}>
                    <ArrowBackIcon fontSize="large" />
                  </IconButton>
                </Grid>
              )}
              <Grid item xs>
                <Typography variant="h2">{title}</Typography>

                {Boolean(breadcrumbItems.length) && (
                  <Breadcrumb items={breadcrumbItems} />
                )}
              </Grid>
              <Grid item>
                <Stack direction="row" alignItems="center">
                  <ConditionalRender condition={chips.length}>
                    <Chips items={chips} />
                  </ConditionalRender>

                  <ConditionalRender condition={plantSelectorConfig}>
                    <PlantSelector config={plantSelectorConfig} />
                  </ConditionalRender>

                  <ConditionalRender condition={dateRangeConfig}>
                    <DateRangeSelector config={dateRangeConfig} />
                  </ConditionalRender>

                  <ConditionalRender condition={primaryIcons}>
                    <PrimaryIcons icons={primaryIcons} />
                  </ConditionalRender>
                </Stack>
              </Grid>
            </Grid>

            <Divider />

            <ConditionalRender condition={shouldRenderSecondaryMenu}>
              <Grid
                container
                sx={{ backgroundColor: "#394586", height: 67 }}
                p={1}
              >
                <Grid item xs>
                  <ConditionalRender condition={secondaryMenu}>
                    <SecondaryMenu menuItems={secondaryMenu} />
                  </ConditionalRender>
                </Grid>

                <Grid item>
                  <ConditionalRender
                    condition={parentAssetsConfig?.assets?.length}
                  >
                    <ParentAssets config={parentAssetsConfig} />
                  </ConditionalRender>
                </Grid>
              </Grid>
            </ConditionalRender>
          </>
        ) : (
          <></>
        )}
        <Box
          flex={1}
          sx={{ overflowY: "auto", padding: 0 }}
          ref={childrenContainerRef}
        >
          {children}
        </Box>
      </Box>
    </SecondaryHeaderContext.Provider>
  );
};

export default SecondaryHeader;

export { SecondaryHeaderContext };
